/**
 * Плагин метрик для CSElement
 * Собирает статистику производительности и использования
 */

import { CSElementPlugin, LifecycleHooks, PluginOptions } from '../types/plugin-interfaces';
import { ICSElement } from '../types/interfaces';
import { CSElement } from '../core/CSElement';

/**
 * Типы метрик
 */
export enum MetricType {
  COUNTER = 'counter',
  GAUGE = 'gauge',
  HISTOGRAM = 'histogram',
  TIMER = 'timer'
}

/**
 * Метрика
 */
export interface Metric {
  name: string;
  type: MetricType;
  value: number;
  timestamp: number;
  tags?: Record<string, string>;
  description?: string;
}

/**
 * Гистограмма для измерения распределения значений
 */
export interface Histogram {
  count: number;
  sum: number;
  min: number;
  max: number;
  avg: number;
  buckets: { [key: string]: number };
  percentiles: { [key: string]: number };
}

/**
 * Таймер для измерения времени выполнения
 */
export interface Timer {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  tags?: Record<string, string>;
}

/**
 * Статистика операций
 */
export interface OperationStats {
  totalCount: number;
  successCount: number;
  errorCount: number;
  totalDuration: number;
  averageDuration: number;
  minDuration: number;
  maxDuration: number;
  lastExecuted: number;
  histogram: Histogram;
}

/**
 * Конфигурация метрик
 */
export interface MetricsConfig {
  enabled: boolean;
  collectInterval: number;    // Интервал сбора метрик в мс
  retentionPeriod: number;   // Период хранения метрик в мс
  maxMetrics: number;        // Максимальное количество метрик
  enabledMetrics: string[];  // Включенные метрики
  histogramBuckets: number[]; // Бакеты для гистограммы
  enableTimers: boolean;     // Включить таймеры
  enableCounters: boolean;   // Включить счетчики
  enableGauges: boolean;     // Включить датчики
}

export interface FunctionStats {
    totalCount: number;
    successCount: number;
    errorCount: number;
    averageDuration: number;
    totalDuration: number;
    lastExecuted?: Date;
}

/**
 * Плагин метрик
 */
export class MetricsPlugin implements CSElementPlugin {
  public readonly name = 'MetricsPlugin';
  public readonly version = '1.0.0';
  public readonly description = 'Плагин для сбора метрик производительности';

  private config: MetricsConfig = {
    enabled: true,
    collectInterval: 60000, // 1 минута
    retentionPeriod: 3600000, // 1 час
    maxMetrics: 10000,
    enabledMetrics: ['*'],
    histogramBuckets: [0.1, 0.5, 1, 2.5, 5, 10, 25, 50, 100, 250, 500, 1000],
    enableTimers: true,
    enableCounters: true,
    enableGauges: true
  };

  private metrics = new Map<string, Metric[]>();
  private counters = new Map<string, number>();
  private gauges = new Map<string, number>();
  private histograms = new Map<string, Histogram>();
  private timers = new Map<string, Timer>();
  private operationStats = new Map<string, OperationStats>();
  private functionStats = new Map<string, FunctionStats>();

  private collectTimer?: NodeJS.Timeout;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(options?: PluginOptions) {
    if (options?.config) {
      this.config = { ...this.config, ...options.config };
    }

    if (this.config.enabled) {
      this.startCollectionTimer();
      this.startCleanupTimer();
    }
  }

  install(CSElementClass: typeof CSElement): void {
    console.log(`MetricsPlugin установлен для ${CSElementClass.name}`);
  }

  getLifecycleHooks(): LifecycleHooks {
    return {
      afterCreate: async (_element: ICSElement) => {
        if (this.config.enabled) {
          this.incrementCounter('elements.created');
          this.setGauge('elements.total', this.getElementCount());
        }
      },

      beforeDestroy: async (_element: ICSElement) => {
        if (this.config.enabled) {
          this.incrementCounter('elements.destroyed');
          this.setGauge('elements.total', this.getElementCount() - 1);
        }
      },

      afterSetData: async (_element: ICSElement, key: string) => {
        if (this.config.enabled) {
          this.incrementCounter('data.operations.set');
          this.incrementCounter(`data.keys.${key}.set`);
        }
      },

      afterDeleteData: async (_element: ICSElement, key: string) => {
        if (this.config.enabled) {
          this.incrementCounter('data.operations.delete');
          this.incrementCounter(`data.keys.${key}.delete`);
        }
      },

      afterAddElement: async (_parent: ICSElement, child: ICSElement) => {
        if (this.config.enabled) {
          this.incrementCounter('elements.added');
          this.recordHistogram('element.depth', this.getElementDepth(child));
        }
      },

      afterRemoveElement: async (_parent: ICSElement, _child: ICSElement) => {
        if (this.config.enabled) {
          this.incrementCounter('elements.removed');
        }
      }
    };
  }

  /**
   * Увеличение счетчика
   */
  incrementCounter(name: string, value: number = 1, tags?: Record<string, string>): void {
    if (!this.config.enableCounters || !this.isMetricEnabled(name)) {
      return;
    }

    const current = this.counters.get(name) || 0;
    this.counters.set(name, current + value);

    this.recordMetric({
      name,
      type: MetricType.COUNTER,
      value: current + value,
      timestamp: Date.now(),
      tags
    });
  }

  /**
   * Установка значения датчика
   */
  setGauge(name: string, value: number, tags?: Record<string, string>): void {
    if (!this.config.enableGauges || !this.isMetricEnabled(name)) {
      return;
    }

    this.gauges.set(name, value);

    this.recordMetric({
      name,
      type: MetricType.GAUGE,
      value,
      timestamp: Date.now(),
      tags
    });
  }

  /**
   * Запись значения в гистограмму
   */
  recordHistogram(name: string, value: number, tags?: Record<string, string>): void {
    if (!this.isMetricEnabled(name)) {
      return;
    }

    let histogram = this.histograms.get(name);
    if (!histogram) {
      histogram = {
        count: 0,
        sum: 0,
        min: Infinity,
        max: -Infinity,
        avg: 0,
        buckets: {},
        percentiles: {}
      };
      this.histograms.set(name, histogram);
    }

    // Обновляем гистограмму
    histogram.count++;
    histogram.sum += value;
    histogram.min = Math.min(histogram.min, value);
    histogram.max = Math.max(histogram.max, value);
    histogram.avg = histogram.sum / histogram.count;

    // Обновляем бакеты
    for (const bucket of this.config.histogramBuckets) {
      const bucketKey = `le_${bucket}`;
      if (value <= bucket) {
        histogram.buckets[bucketKey] = (histogram.buckets[bucketKey] || 0) + 1;
      }
    }

    this.recordMetric({
      name,
      type: MetricType.HISTOGRAM,
      value,
      timestamp: Date.now(),
      tags
    });
  }

  /**
   * Запуск таймера
   */
  startTimer(name: string, tags?: Record<string, string>): string {
    if (!this.config.enableTimers || !this.isMetricEnabled(name)) {
      return '';
    }

    const timerId = `${name}_${Date.now()}_${Math.random()}`;
    const timer: Timer = {
      name,
      startTime: Date.now(),
      tags
    };

    this.timers.set(timerId, timer);
    return timerId;
  }

  /**
   * Остановка таймера
   */
  stopTimer(timerId: string): number | null {
    const timer = this.timers.get(timerId);
    if (!timer) {
      return null;
    }

    timer.endTime = Date.now();
    timer.duration = timer.endTime - timer.startTime;

    // Записываем в статистику операций
    this.recordOperationStats(timer.name, timer.duration, true);

    // Записываем в гистограмму
    this.recordHistogram(`${timer.name}.duration`, timer.duration, timer.tags);

    // Записываем метрику
    this.recordMetric({
      name: `${timer.name}.timer`,
      type: MetricType.TIMER,
      value: timer.duration,
      timestamp: timer.endTime,
      tags: timer.tags
    });

    this.timers.delete(timerId);
    return timer.duration;
  }

  /**
   * Измерение времени выполнения функции
   */
  async timeFunction<T>(name: string, fn: () => Promise<T>, tags?: Record<string, string>): Promise<T> {
    const startTime = Date.now();
    let success = false;

    try {
      const result = await fn();
      success = true;
      return result;
    } catch (error) {
      this.incrementCounter(`${name}.errors`, 1, tags);
      throw error;
    } finally {
      const duration = Date.now() - startTime;
      this.recordOperationStats(name, duration, success);
      
      // Записываем в гистограмму
      this.recordHistogram(`${name}.duration`, duration, tags);

      // Записываем метрику
      this.recordMetric({
        name: `${name}.timer`,
        type: MetricType.TIMER,
        value: duration,
        timestamp: Date.now(),
        tags
      });
    }
  }

  /**
   * Запись статистики операций
   */
  private recordOperationStats(operation: string, duration: number, success: boolean): void {
    let stats = this.operationStats.get(operation);
    if (!stats) {
      stats = {
        totalCount: 0,
        successCount: 0,
        errorCount: 0,
        totalDuration: 0,
        averageDuration: 0,
        minDuration: Infinity,
        maxDuration: 0,
        lastExecuted: 0,
        histogram: {
          count: 0,
          sum: 0,
          min: Infinity,
          max: -Infinity,
          avg: 0,
          buckets: {},
          percentiles: {}
        }
      };
      this.operationStats.set(operation, stats);
    }

    stats.totalCount++;
    if (success) {
      stats.successCount++;
    } else {
      stats.errorCount++;
    }

    stats.totalDuration += duration;
    stats.averageDuration = stats.totalDuration / stats.totalCount;
    stats.minDuration = Math.min(stats.minDuration, duration);
    stats.maxDuration = Math.max(stats.maxDuration, duration);
    stats.lastExecuted = Date.now();

    // Обновляем гистограмму
    const histogram = stats.histogram;
    histogram.count++;
    histogram.sum += duration;
    histogram.min = Math.min(histogram.min, duration);
    histogram.max = Math.max(histogram.max, duration);
    histogram.avg = histogram.sum / histogram.count;
  }

  /**
   * Запись метрики
   */
  private recordMetric(metric: Metric): void {
    const metrics = this.metrics.get(metric.name) || [];
    metrics.push(metric);

    // Ограничиваем количество метрик
    if (metrics.length > this.config.maxMetrics) {
      metrics.shift();
    }

    this.metrics.set(metric.name, metrics);
  }

  /**
   * Проверка, включена ли метрика
   */
  private isMetricEnabled(name: string): boolean {
    if (this.config.enabledMetrics.includes('*')) {
      return true;
    }

    return this.config.enabledMetrics.some(pattern => {
      if (pattern.includes('*')) {
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        return regex.test(name);
      }
      return pattern === name;
    });
  }

  /**
   * Получение количества элементов (заглушка)
   */
  private getElementCount(): number {
    // В реальной реализации это должно получать количество из CSElement
    return 0;
  }

  /**
   * Получение глубины элемента (заглушка)
   */
  private getElementDepth(_element: ICSElement): number {
    // В реальной реализации это должно вычислять глубину элемента
    return 1;
  }

  /**
   * Получение всех метрик
   */
  getAllMetrics(): Map<string, Metric[]> {
    return new Map(this.metrics);
  }

  /**
   * Получение метрик по имени
   */
  getMetrics(name: string): Metric[] {
    return this.metrics.get(name) || [];
  }

  /**
   * Получение последних метрик
   */
  getLatestMetrics(limit: number = 100): Metric[] {
    const allMetrics: Metric[] = [];
    
    for (const metrics of this.metrics.values()) {
      allMetrics.push(...metrics);
    }

    return allMetrics
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
  }

  /**
   * Получение счетчиков
   */
  getCounters(): Map<string, number> {
    return new Map(this.counters);
  }

  /**
   * Получение датчиков
   */
  getGauges(): Map<string, number> {
    return new Map(this.gauges);
  }

  /**
   * Получение гистограмм
   */
  getHistograms(): Map<string, Histogram> {
    return new Map(this.histograms);
  }

  /**
   * Получение статистики операций
   */
  getOperationStats(): Map<string, OperationStats> {
    return new Map(this.operationStats);
  }

  /**
   * Получение сводной статистики
   */
  getSummary(): {
    totalMetrics: number;
    counters: number;
    gauges: number;
    histograms: number;
    activeTimers: number;
    operations: number;
  } {
    return {
      totalMetrics: Array.from(this.metrics.values()).reduce((sum, metrics) => sum + metrics.length, 0),
      counters: this.counters.size,
      gauges: this.gauges.size,
      histograms: this.histograms.size,
      activeTimers: this.timers.size,
      operations: this.operationStats.size
    };
  }

  /**
   * Экспорт метрик в формате Prometheus
   */
  exportPrometheus(): string {
    let output = '';

    // Экспорт счетчиков
    for (const [name, value] of this.counters) {
      output += `# TYPE ${name} counter\n`;
      output += `${name} ${value}\n\n`;
    }

    // Экспорт датчиков
    for (const [name, value] of this.gauges) {
      output += `# TYPE ${name} gauge\n`;
      output += `${name} ${value}\n\n`;
    }

    // Экспорт гистограмм
    for (const [name, histogram] of this.histograms) {
      output += `# TYPE ${name} histogram\n`;
      output += `${name}_count ${histogram.count}\n`;
      output += `${name}_sum ${histogram.sum}\n`;
      
      for (const [bucket, count] of Object.entries(histogram.buckets)) {
        const le = bucket.replace('le_', '');
        output += `${name}_bucket{le="${le}"} ${count}\n`;
      }
      output += `${name}_bucket{le="+Inf"} ${histogram.count}\n\n`;
    }

    return output;
  }

  /**
   * Очистка старых метрик
   */
  private cleanupOldMetrics(): void {
    const now = Date.now();
    const cutoff = now - this.config.retentionPeriod;

    for (const [name, metrics] of this.metrics) {
      const filtered = metrics.filter(metric => metric.timestamp > cutoff);
      if (filtered.length !== metrics.length) {
        this.metrics.set(name, filtered);
      }
    }

    // Очищаем завершенные таймеры
    for (const [id, timer] of this.timers) {
      if (timer.endTime && timer.endTime < cutoff) {
        this.timers.delete(id);
      }
    }
  }

  /**
   * Запуск таймера сбора метрик
   */
  private startCollectionTimer(): void {
    this.collectTimer = setInterval(() => {
      this.collectSystemMetrics();
    }, this.config.collectInterval);
  }

  /**
   * Запуск таймера очистки
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanupOldMetrics();
    }, this.config.retentionPeriod / 10); // Очищаем каждую 1/10 от периода хранения
  }

  /**
   * Сбор системных метрик
   */
  private collectSystemMetrics(): void {
    if (!this.config.enabled) {
      return;
    }

    // Метрики памяти (если доступны)
    if (typeof process !== 'undefined' && process.memoryUsage) {
      const memory = process.memoryUsage();
      this.setGauge('system.memory.rss', memory.rss);
      this.setGauge('system.memory.heapUsed', memory.heapUsed);
      this.setGauge('system.memory.heapTotal', memory.heapTotal);
      this.setGauge('system.memory.external', memory.external);
    }

    // Метрики времени работы
    if (typeof process !== 'undefined' && process.uptime) {
      this.setGauge('system.uptime', process.uptime() * 1000);
    }

    // Метрики плагина
    this.setGauge('metrics.total_count', Array.from(this.metrics.values()).reduce((sum, metrics) => sum + metrics.length, 0));
    this.setGauge('metrics.counters_count', this.counters.size);
    this.setGauge('metrics.gauges_count', this.gauges.size);
    this.setGauge('metrics.histograms_count', this.histograms.size);
    this.setGauge('metrics.active_timers', this.timers.size);
  }

  /**
   * Остановка таймеров
   */
  private stopTimers(): void {
    if (this.collectTimer) {
      clearInterval(this.collectTimer);
      this.collectTimer = undefined;
    }

    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = undefined;
    }
  }

  /**
   * Сброс всех метрик
   */
  reset(): void {
    this.metrics.clear();
    this.counters.clear();
    this.gauges.clear();
    this.histograms.clear();
    this.timers.clear();
    this.operationStats.clear();
    this.functionStats.clear();
  }

  /**
   * Обновление конфигурации
   */
  updateConfig(config: Partial<MetricsConfig>): void {
    const oldEnabled = this.config.enabled;
    this.config = { ...this.config, ...config };

    // Перезапускаем таймеры если изменились настройки
    if (this.config.enabled && (!oldEnabled || config.collectInterval)) {
      this.stopTimers();
      this.startCollectionTimer();
      this.startCleanupTimer();
    } else if (!this.config.enabled && oldEnabled) {
      this.stopTimers();
    }

    // Очищаем метрики если отключили
    if (!this.config.enabled) {
      this.reset();
    }
  }

  /**
   * Получение конфигурации
   */
  getConfig(): MetricsConfig {
    return { ...this.config };
  }

  /**
   * Очистка ресурсов
   */
  destroy(): void {
    this.stopTimers();
    this.reset();
  }

  measureFunction<T>(functionName: string, fn: () => Promise<T>): Promise<T> {
    const startTime = Date.now();
    
    return fn().then(
        (result) => {
            const duration = Date.now() - startTime;
            this.recordExecution(functionName, duration, true);
            return result;
        },
        (error) => {
            const duration = Date.now() - startTime;
            this.recordExecution(functionName, duration, false);
            throw error;
        }
    );
  }

  private recordExecution(functionName: string, duration: number, success: boolean): void {
    let stats = this.functionStats.get(functionName);
    
    if (!stats) {
        stats = {
            totalCount: 0,
            successCount: 0,
            errorCount: 0,
            averageDuration: 0,
            totalDuration: 0
        };
        this.functionStats.set(functionName, stats);
    }

    stats.totalCount++;
    stats.totalDuration += duration;
    stats.averageDuration = stats.totalDuration / stats.totalCount;
    stats.lastExecuted = new Date();

    if (success) {
        stats.successCount++;
    } else {
        stats.errorCount++;
    }
  }

  getStats(functionName: string): FunctionStats | undefined {
    return this.functionStats.get(functionName);
  }

  getAllStats(): Map<string, FunctionStats> {
    return new Map(this.functionStats);
  }

  resetStats(functionName?: string): void {
    if (functionName) {
        this.functionStats.delete(functionName);
    } else {
        this.functionStats.clear();
    }
  }
}

/**
 * Фабричная функция для создания плагина метрик
 */
export function metricsPlugin(config?: Partial<MetricsConfig>): MetricsPlugin {
  return new MetricsPlugin({ config });
} 